/**
 * Fret-triad recognition — the guitar track's rungs 2 & 3.
 *
 * The user hears a triad (a quality in rung 2, a specific inversion in rung 3).
 * The bass note is shown as a `given` dot on the lowest string of the set so
 * the user has a pitch anchor; they tap the remaining two chord tones on the
 * upper two strings. `notesToPlace` is 2, so the route uses an explicit
 * "Check (n/2)" submit.
 *
 * For rung 2 (shape) the bass is always the root (root position). For rung 3
 * (inversion) the bass carries the inversion's defining note (root/3rd/5th),
 * which encodes the inversion: the user sees which chord tone is in the bass
 * and must place the other two voices above it.
 *
 * Audio is context-free quality/inversion: arpeggio bottom-up, then a block
 * chord. Graded by `gradeFretboard`.
 *
 * See docs/design/guitar-harmony-v1.md §5.0 / §6.
 */

import { AudioPlanBuilder, type AudioPlan } from "./audio-plan.js";
import { Rng, type Exercise, type FretboardPrompt } from "./exercise.js";
import { buildChord } from "./utils.js";
import {
  STANDARD_TUNING,
  STRING_SETS,
  midiAt,
  positionsOnStringSet,
  type FretPosition,
  type PitchClass,
} from "@music-app/theory-engine";

const QUALITY_LABELS: Record<string, string> = {
  maj: "Major",
  min: "Minor",
  dim: "Diminished",
  aug: "Augmented",
};

const INVERSION_NAMES = ["Root position", "First inversion", "Second inversion"];
const INVERSION_DESC = ["root in the bass", "third in the bass", "fifth in the bass"];

export interface FretTriadParams {
  quality: string;
  /** Index into STRING_SETS (top-set-first). */
  stringSet: number;
  /** 0 = root position, 1 = first, 2 = second. */
  inversion: number;
}

/** Whether the bass note is graded — rung 3 pins it, rung 2 does not. */
export type FretTriadKind = "shape" | "inversion";

const pcOf = (midi: number): PitchClass => ((((midi % 12) + 12) % 12) as PitchClass);

/** Move the bottom `inv` notes up an octave, then re-sort low→high. */
function applyInversion(chord: number[], inv: number): number[] {
  const result = [...chord];
  for (let i = 0; i < inv; i++) result[i] = result[i]! + 12;
  return result.sort((a, b) => a - b);
}

/**
 * Close-position voicing for the display flash on a wrong answer.
 *
 * Enumerates every fret position (0–fretCount) of each pitch class on its
 * assigned string, then tries all combinations while keeping MIDI ascending
 * (close voicing, bass lowest). Picks the combination with the smallest fret
 * span so the displayed shape is as compact as physically possible.
 *
 * The greedy alternative (prevMidi = −∞) placed the bass at fret 0 whenever
 * its pc matched the open string, while subsequent voices landed 10–11 frets
 * away — producing unplayable shapes. This approach finds the best octave for
 * the bass automatically (at most 2 occurrences per string on a 15-fret neck,
 * so at most 8 combinations total).
 *
 * Returns `[]` only if no ascending-MIDI placement fits all three voices on the
 * neck, which cannot happen for standard triads in the used root-midi range.
 */
function findCompactVoicing(stringSet: readonly number[], pcsBottomToTop: PitchClass[]): FretPosition[] {
  const sorted = [...stringSet].sort((a, b) => a - b);

  const candidates: FretPosition[][] = sorted.map((stringIndex, i) => {
    const pc = pcsBottomToTop[i]!;
    const out: FretPosition[] = [];
    for (let fret = 0; fret <= STANDARD_TUNING.fretCount; fret++) {
      if (pcOf(midiAt(STANDARD_TUNING, { stringIndex, fret })) === pc) {
        out.push({ stringIndex, fret });
      }
    }
    return out;
  });

  const [cand0, cand1, cand2] = candidates;
  if (!cand0 || !cand1 || !cand2) return [];

  let best: FretPosition[] = [];
  let bestSpan = Infinity;

  for (const p0 of cand0) {
    const m0 = midiAt(STANDARD_TUNING, p0);
    for (const p1 of cand1) {
      const m1 = midiAt(STANDARD_TUNING, p1);
      if (m1 < m0) continue;
      for (const p2 of cand2) {
        const m2 = midiAt(STANDARD_TUNING, p2);
        if (m2 < m1) continue;
        const span = Math.max(p0.fret, p1.fret, p2.fret) - Math.min(p0.fret, p1.fret, p2.fret);
        if (span < bestSpan) { bestSpan = span; best = [p0, p1, p2]; }
      }
    }
  }

  return best;
}

export function generateFretTriad(
  itemId: string,
  params: FretTriadParams,
  seed: number,
  kind: FretTriadKind,
): Exercise {
  const rng = new Rng(seed);
  const stringSet = STRING_SETS[params.stringSet];
  if (!stringSet) throw new Error(`Unknown string set index: ${params.stringSet}`);

  // Context-free root, kept low so an inverted voicing still sits on the neck.
  const rootMidi = 52 + rng.int(8); // E3..B3
  const chord = buildChord(rootMidi, params.quality); // [root, 3rd, 5th]
  if (chord.length !== 3) throw new Error(`Not a triad: ${params.quality}`);

  const chordPcs = chord.map(pcOf); // [rootPc, thirdPc, fifthPc]
  const inversion = kind === "inversion" ? params.inversion : 0;
  const bassPc = chordPcs[inversion]!;
  const heard = applyInversion(chord, inversion);

  const others = chordPcs.filter((pc) => pc !== bassPc);

  // Bass-anchored voicing: bass sits on the lowest string; user places the
  // other two tones on the upper strings. This gives a pitch anchor without
  // requiring perfect pitch.
  const sortedStrings = [...stringSet].sort((a, b) => a - b);
  const upperStrings = sortedStrings.slice(1);
  const pcsBottomToTop: PitchClass[] = [bassPc, ...others] as PitchClass[];
  const allVoicePositions = findCompactVoicing(stringSet, pcsBottomToTop);
  if (allVoicePositions.length < 3) {
    throw new Error(`No close-voiced triad found for ${params.quality} inv=${inversion} on stringSet ${params.stringSet}`);
  }
  const givenPosition = allVoicePositions[0]!;
  const solution = allVoicePositions.slice(1);

  const acceptedPositions = others.flatMap((pc) =>
    positionsOnStringSet(STANDARD_TUNING, pc, upperStrings),
  );

  const allFrets = allVoicePositions.map((p) => p.fret);
  const listenFretWindow = {
    from: Math.max(0, Math.min(...allFrets) - 1),
    to: Math.min(STANDARD_TUNING.fretCount, Math.max(...allFrets) + 1),
  };

  const fretboard: FretboardPrompt = {
    tuning: "standard",
    given: [givenPosition],
    allowedStrings: upperStrings,
    notesToPlace: 2,
    acceptedPositions,
    solution,
    listenFretWindow,
  };

  // Audio: arpeggio bottom-up (so the bass is heard first), then a block chord.
  const builder = new AudioPlanBuilder();
  for (const note of heard) {
    builder.addNote(note, 0.55, 0.2, "melody");
    builder.advance(0.3);
  }
  builder.advance(0.4);
  builder.addChord(heard, 1.4, 0.13, "harmony");
  builder.advance(1.5);
  const audio = builder.build();

  const quality = QUALITY_LABELS[params.quality] ?? params.quality;
  const explanation =
    kind === "inversion"
      ? `${quality} — ${INVERSION_NAMES[inversion]} (${INVERSION_DESC[inversion]}).`
      : `${quality} triad (${params.quality}).`;
  const prompt =
    kind === "inversion"
      ? "Place the inversion you heard on the highlighted strings."
      : "Build the triad you heard on the highlighted strings.";

  return {
    itemId,
    exerciseType: kind === "inversion" ? "fret-triad-inversion-recognition" : "fret-triad-recognition",
    audio,
    prompt,
    options: [],
    correctAnswerId: "",
    meta: {
      seed,
      explanation,
      distractorCount: 0,
      teachAudio: buildFretTriadTeachPlan(heard, kind),
      fretboard,
      playbackMidis: [...heard],
    },
  };
}

/**
 * Wrong-answer teach plan: a slower arpeggio so each chord tone is hearable,
 * then (for inversions) the bass note alone — the inversion-defining tone —
 * before the block chord.
 */
function buildFretTriadTeachPlan(heard: number[], kind: FretTriadKind): AudioPlan {
  const builder = new AudioPlanBuilder();
  for (const note of heard) {
    builder.addNote(note, 0.8, 0.22, "melody");
    builder.advance(0.5);
  }
  builder.advance(0.3);
  if (kind === "inversion") {
    builder.addNote(heard[0]!, 1.2, 0.24, "target");
    builder.advance(1.4);
  }
  builder.addChord(heard, 1.5, 0.13, "harmony");
  builder.advance(1.6);
  return builder.build();
}
